/**
 * Anlagevorschläge — die Rückfrage vor dem Zähler (HM-058).
 *
 * **Kein Warnkasten.** Ein Vorschlag sagt nicht, dass etwas fehlt, sondern dass
 * etwas da ist: Ein Gerät liefert Werte, aus denen ein Zähler werden könnte. Er
 * ist deshalb eine Karte in Ruhefarbe und kein rotes Feld — und er verschwindet,
 * sobald jemand entschieden hat.
 *
 * **Zwei Wege, und beide sind echte Antworten.** „Anlegen" ist der erwartete
 * Fall; „Nicht anlegen" ist es genauso oft, denn nicht jede Messung soll ein
 * geführter Zähler werden — ein Lesekopf am Nachbarzähler, eine Kontrollmessung,
 * ein Kanal, der versehentlich bestückt ist. Wer ablehnt, wird nicht beim
 * nächsten Sendetakt erneut gefragt.
 *
 * **Die Zählernummer steht im Bestätigungsformular**, nicht danach. Wer den
 * Vorschlag sieht, steht oft gerade vor dem Zählerschrank; sie dort einzutragen
 * spart den zweiten Weg und macht aus dem Entwurf sofort einen fertigen Zähler.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Input, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { navigate, type Route } from "../lib/router.js";
import { registerKeyLabel, registerRoleLabel } from "../lib/meter-labels.js";
import { mutedText } from "../lib/list-styles.js";
import { useSiteAccess } from "../lib/site-access.js";

type MeterSuggestion = ApiComponents["schemas"]["MeterSuggestion"];

export function MeterSuggestions({
  siteId,
  suggestions,
}: {
  siteId: string;
  suggestions: readonly MeterSuggestion[];
}) {
  /* Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Ein Vorschlag ist
     eine Frage, und wer nur lesen darf, kann sie nicht beantworten. */
  const rights = useSiteAccess();
  if (suggestions.length === 0 || !rights.canWrite) return null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 15,
            fontWeight: 700,
            color: "var(--text-strong)",
          }}
        >
          {suggestions.length === 1 ? "Ein Gerät meldet Zählerwerte" : "Geräte melden Zählerwerte"}
        </h2>
        <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>
          Bestätige, was davon als Zähler geführt werden soll. Angelegt wird nichts von
          selbst — an einem Zähler hängt jede spätere Abrechnung.
        </p>
      </div>

      {suggestions.map((suggestion) => (
        <SuggestionCard key={suggestion.id} siteId={siteId} suggestion={suggestion} />
      ))}
    </section>
  );
}

function SuggestionCard({
  siteId,
  suggestion,
}: {
  siteId: string;
  suggestion: MeterSuggestion;
}) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(suggestion.proposedLabel);
  const [meterNumber, setMeterNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  /**
   * **Ein Messwerk im Gerät wird nicht nach einer Nummer gefragt** (HM-112). Der
   * Vorschlag sagt es, wenn kein Zählwerk eine gemeldete Kennzahl trägt — ein
   * EVCC-Ladepunkt hat kein Typenschild, und ein Feld „Vom Typenschild" an ihm
   * schickte jemanden auf die Suche nach etwas, das es nicht gibt.
   */
  const inDevice = suggestion.builtIntoDevice;

  const invalidate = async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: ["meter-suggestions"] });
    await client.invalidateQueries({ queryKey: ["meters"] });
    await client.invalidateQueries({ queryKey: ["metering-points"] });
  };

  const accept = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await api.POST(
        "/meter-suggestions/{suggestionId}/accept",
        {
          params: { path: { suggestionId: suggestion.id } },
          body: {
            ...(label.trim() !== suggestion.proposedLabel ? { label: label.trim() } : {}),
            ...(meterNumber.trim().length > 0 ? { meterNumber: meterNumber.trim() } : {}),
          },
        },
      );
      if (failure || !data) {
        throw new Error(apiErrorMessage(failure) ?? "Der Zähler ließ sich nicht anlegen.");
      }
      return data;
    },
    onSuccess: async (meter) => {
      await invalidate();
      /**
       * **Weiter zum Ergebnis.** Wer bestätigt hat, will den Zähler sehen — und
       * dort steht, was als Nächstes fehlt (die Zählernummer, wenn er sie hier
       * nicht eingetragen hat).
       */
      /* Ohne Zählwerk und ohne Zeitraum: Die Detailseite nimmt dafür das erste
         Zählwerk und die üblichen 24 Stunden. */
      const route: Route = {
        kind: "meter",
        siteId,
        meterId: meter.id,
        tab: "overview",
        register: null,
        range: "24h",
      };
      navigate(route);
    },
    onError: (failure: Error) => setError(failure.message),
  });

  const dismiss = useMutation({
    mutationFn: async () => {
      const { error: failure } = await api.POST("/meter-suggestions/{suggestionId}/dismiss", {
        params: { path: { suggestionId: suggestion.id } },
      });
      if (failure) {
        throw new Error(apiErrorMessage(failure) ?? "Der Vorschlag ließ sich nicht ablehnen.");
      }
    },
    onSuccess: invalidate,
    onError: (failure: Error) => setError(failure.message),
  });

  const adding = suggestion.kind === "add_register";

  return (
    <Card>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--sp-4)", alignItems: "flex-start" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: "var(--radius-md)",
            background: "var(--surface-inset)",
            color: "var(--text-muted)",
          }}
        >
          <icons.IconGauge size={20} />
        </span>

        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: "var(--text-strong)" }}>
              {suggestion.proposedLabel}
            </span>
            {/**
             * **Grau, nicht gelb.** Ein Vorschlag ist keine Störung — die Marke
             * sagt, was hier zu tun ist, und nicht, dass etwas schiefging.
             */}
            <Badge tone="neutral">
              {adding ? "Zählwerk ergänzen" : "Neuer Zähler"}
            </Badge>
            {!adding && inDevice && <Badge tone="neutral">Messwerk im Gerät</Badge>}
          </div>

          <p style={{ ...mutedText, marginTop: 4 }}>
            {suggestion.deviceName}
            {suggestion.channelName !== null && ` · ${suggestion.channelName}`}
            {adding && suggestion.meterLabel !== null && ` · gehört zu „${suggestion.meterLabel}"`}
          </p>

          {/**
           * **Was das Gerät über den Zähler sagt, steht dabei** — Geräte-ID und
           * Hersteller aus dem Telegramm. Sie sind der Grund, warum jemand den
           * Vorschlag einem Zähler im Schrank zuordnen kann; ohne sie stünde
           * dort nur ein Kanalname.
           */}
          {(suggestion.serialFromDevice !== null || suggestion.manufacturer !== null) && (
            <p
              style={{
                ...mutedText,
                marginTop: 2,
                fontFamily: "var(--font-mono)",
                fontSize: 11.5,
              }}
            >
              {[suggestion.manufacturer, suggestion.serialFromDevice].filter(Boolean).join(" · ")}
            </p>
          )}

          <ul
            style={{
              listStyle: "none",
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              padding: 0,
              margin: "var(--sp-3) 0 0",
            }}
          >
            {suggestion.registers.map((register) => (
              <li
                key={register.datapointId}
                title={registerKeyLabel(register)}
                style={{
                  fontSize: 12,
                  padding: "3px 9px",
                  borderRadius: "var(--radius-sm)",
                  background: "var(--surface-inset)",
                  color: "var(--text-body)",
                }}
              >
                {registerRoleLabel(register.role)}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
          {!open && (
            <>
              <Button size="sm" onClick={() => setOpen(true)}>
                {adding ? "Zählwerk ergänzen …" : "Zähler anlegen …"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={dismiss.isPending}
                onClick={() => {
                  setError(null);
                  dismiss.mutate();
                }}
              >
                Nicht anlegen
              </Button>
            </>
          )}
        </div>
      </div>

      {error !== null && (
        <div style={{ marginTop: "var(--sp-3)" }}>
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {open && (
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-3)",
            marginTop: "var(--sp-4)",
            paddingTop: "var(--sp-4)",
            borderTop: "1px solid var(--border-subtle)",
          }}
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            accept.mutate();
          }}
        >
          {/**
           * **Beim Ergänzen gibt es nichts zu benennen.** Der Zähler steht
           * bereits; hier kommt nur ein Zählwerk dazu. Ein Namensfeld wäre ein
           * Feld, dessen Wirkung niemand sieht.
           */}
          {!adding && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                gap: "var(--sp-3)",
              }}
            >
              <Input
                label="Bezeichnung"
                value={label}
                maxLength={120}
                onChange={(event) => setLabel(event.target.value)}
              />
              {inDevice ? (
                <p style={{ ...mutedText, alignSelf: "end" }}>
                  Das Messwerk steckt im Gerät und hat kein Typenschild — der Zähler ist ohne
                  Zählernummer vollständig.
                </p>
              ) : (
                <Input
                  label="Zählernummer"
                  value={meterNumber}
                  maxLength={64}
                  hint="Vom Typenschild. Ohne sie bleibt der Zähler ein Entwurf."
                  onChange={(event) => setMeterNumber(event.target.value)}
                />
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            <Button type="submit" size="sm" disabled={accept.isPending}>
              {accept.isPending
                ? "Wird angelegt …"
                : adding
                  ? "Zählwerk ergänzen"
                  : "Zähler anlegen"}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}
